/**
 * @fileoverview Root router and provider composition for the system-web application.
 * @module system-web/app
 *
 * Responsibilities:
 * - Compose application-wide providers for auth, theme, local DB, and routing.
 * - Register top-level system-web routes.
 *
 * Dependencies:
 * - react-router-dom
 * - ./components/SystemDBProvider
 * - ./auth/AuthProvider
 *
 * Architecture:
 * - Provider composition keeps local-first DB access available before page-level reads.
 * - Routes render pages that must read through Dexie-backed services rather than direct API calls.
 *
 * Notes:
 * - System library data should be seeded server-side and synced down to clients.
 */

import React, { Suspense, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import type { ReactNode } from 'react';
import { AuthProvider, useAuth } from './auth/AuthProvider';
import { ThemeProvider, useTheme } from '@ui-admin/contexts/ThemeContext';
import { useEffectiveTheme } from './hooks/useEffectiveTheme';
import { FeatureTierProvider } from './contexts/FeatureTierProvider';
import { SystemAppModeProvider } from './contexts/SystemAppModeContext';
import { SystemDBProvider } from './components/SystemDBProvider';
import AdminLayout from './layouts/AdminLayout';
import { BreadcrumbProvider } from './contexts/BreadcrumbContext';
import { AIChatProvider, aiChatService } from '@firesquirrel/ai-chatbot';
import { aiChatPost, aiChatGet } from './services/aiChatApiClient';
import { createReportDefinition } from './services/systemReportDefinitionService';
import type { ReportDefinitionLocal } from '@firesquirrel/local-db';
// NOTE: seedWildlandInspectionTemplate and seedSystemReportCenter were removed.
// Client-side browser seeds are wrong architecture for system library data.
// This data belongs in MongoDB (system cluster), seeded once via a server-side
// migration in api/migrations/, then synced to all users via pull replication.
// TODO: Create api/migrations/seed-system-library-templates.ts before release.
import DashboardPage from './pages/DashboardPage';
import OrganizationsPage from './pages/OrganizationsPage';
import TenantsPage from './pages/TenantsPage';
import UsersPage from './pages/UsersPage';
// Lazy-loaded: LookupsPage pulls in react-icons/gi (~7 MB icon catalog via
// IconPicker). Code-splitting keeps it out of the initial bundle so pages that
// don't need the icon picker (e.g. SystemStatusPage) load significantly faster.
const LookupsPage = React.lazy(() => import('./pages/LookupsPage'));
import SettingsPage from './pages/SettingsPage';
import SystemRolesPage from './pages/SystemRolesPage';
import SystemUserDetailPage from './pages/SystemUserDetailPage';
import AuthCallbackPage from './pages/AuthCallbackPage';
import LoginPage from './pages/LoginPage';
import TiersPage from './pages/TiersPage';
import ModulesPage from './pages/ModulesPage';
import FeatureFlagsPage from './pages/FeatureFlagsPage';
import PortalDefaultsPage from './pages/PortalDefaultsPage';
import AuditLogsPage from './pages/AuditLogsPage';
import CreateTenantWizard from './pages/tenants/wizard/CreateTenantWizard';
import DepartmentDetailPage from './pages/DepartmentDetailPage';
import ImportTemplatesPage from './pages/ImportTemplatesPage';
import UnifiedImportWizardPreviewPage from './pages/UnifiedImportWizardPreviewPage';
import ImportWizardPage from './pages/ImportWizardPage';
import ImportRunsPage from './pages/ImportRunsPage';
import ImportRunDetailPage from './pages/ImportRunDetailPage';
import SystemStatusPage from './pages/SystemStatusPage';
import MongoDbDashboardPage from './pages/MongoDbDashboardPage';
import AiOperationsDashboardPage from './pages/AiOperationsDashboardPage';
import SyncHealthPage from './pages/SyncHealthPage';
import DataRepairPage from './pages/DataRepairPage';
import SystemViolationCodeSetsPage from './pages/SystemViolationCodeSetsPage';
import SystemInspectionTemplatesPage from './pages/SystemInspectionTemplatesPage';
import SystemInspectionTemplateEditorPage from './pages/SystemInspectionTemplateEditorPage';
import InspectionProgramsPage from './pages/InspectionProgramsPage';
// Section 2.3: SystemFormBuilderPage removed \u2014 forms now edited in-canvas only.
import AuthAcceptPage from './pages/AuthAcceptPage';
import BrandSplashPreviewPage from './pages/BrandSplashPreviewPage';
import SsoItHandoffPage from './pages/SsoItHandoffPage';

// Legacy Migration
import LegacyMigrationPage from './pages/migration/LegacyMigrationPage';
import LegacyMigrationRunDetailPage from './pages/migration/LegacyMigrationRunDetailPage';

// Report Center
import SystemDataReportsPage from './pages/reports/SystemDataReportsPage';
import SystemDataReportBuilderPage from './pages/reports/SystemDataReportBuilderPage';
import SystemDashboardsPage from './pages/reports/SystemDashboardsPage';
import SystemDashboardBuilderPage from './pages/reports/SystemDashboardBuilderPage';
import SystemDocumentsPage from './pages/reports/SystemDocumentsPage';
import SystemDocumentBuilderPage from './pages/reports/SystemDocumentBuilderPage';
import SystemEmailTemplateBuilderPage from './pages/reports/SystemEmailTemplateBuilderPage';

/**
 * @component ThemeBootstrap
 *
 * Purpose:
 * - Bridge the Dexie-resolved effective theme into the parent ThemeProvider for system-web.
 * - Ensures the active theme reflects the user override → app default chain.
 *
 * Behavior:
 * - Subscribes to useEffectiveTheme() (Dexie liveQuery-backed).
 * - Calls setThemeId only after Dexie has resolved the authenticated user's local
 *   record. Before that point, localStorage remains the fast paint-time bootstrap.
 * - The bridge does not react to ThemeContext changes, preventing an optimistic
 *   selection from being reverted before Dexie confirms the write.
 * - Dexie is authoritative once the local user record is resolved.
 *
 * Data Source:
 * - local (Dexie via useEffectiveTheme)
 *
 * Constraints:
 * - Must be rendered inside ThemeProvider and AuthProvider.
 * - The `resolved` gate is mandatory: logged-out and not-yet-synced states must
 *   not overwrite the localStorage bootstrap theme.
 */
function ThemeBootstrap({ children }: { children: ReactNode }) {
  const { effectiveThemeId, resolved } = useEffectiveTheme();
  const { themeId, setThemeId } = useTheme();

  useEffect(() => {
    if (!resolved || themeId === effectiveThemeId) return;
    setThemeId(effectiveThemeId);
    // themeId is intentionally excluded to avoid reverting optimistic selections.
  }, [effectiveThemeId, resolved, setThemeId]);

  return <>{children}</>;
}

/**
 * Navigation payload emitted by the AI chat for library/builder/report targets.
 */
type AINavigateData = {
  configType?: string;
  reportType?: string;
  reportDefinitionId?: string;
  dashboardId?: string;
  documentId?: string;
};

/**
 * AI-generated preview payload saved as a draft report definition.
 */
type AIPreviewPayload = Partial<ReportDefinitionLocal>;

/**
 * @component AppContent
 *
 * Purpose:
 * - Renders the authenticated system-web route tree inside the shared providers.
 *
 * Props:
 * - None.
 *
 * Behavior:
 * - Initializes AI chat transport after auth loads and exposes the route structure for all system pages.
 *
 * Data Source:
 * - hybrid
 *
 * Constraints:
 * - Routed pages must keep their own data access local-first.
 *
 * Performance:
 * - Provider initialization happens once at the app shell level.
 */
function AppContent() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, isLoading } = useAuth();
  const isPublicHandoff = location.pathname.startsWith('/external/sso-handoff/');

  // Initialize AI chat service after auth is set up
  useEffect(() => {
    aiChatService.initialize({
      post: aiChatPost,
      get: aiChatGet,
    });
  }, []);

  const handleAISave = useCallback(async (preview: AIPreviewPayload, reportType: string): Promise<string | null> => {
    try {
      const isDashboard = reportType === 'dashboard';
      const newReport = await createReportDefinition({
        name: preview.name || (isDashboard ? 'AI Generated Dashboard' : 'AI Generated Report'),
        description: preview.description || '',
        type: (reportType as 'report' | 'dashboard') || 'report',
        status: 'draft',
        aiGenerationSource: 'ai-generated',
        isInLibrary: true, // AI-generated templates are automatically added to library for seeding
        ...(isDashboard
          ? { dashboardConfig: preview.dashboardConfig || { widgets: [], layout: { columns: 12, rowHeight: 60 } } }
          : { reportConfig: preview.reportConfig || { format: 'tabular', dataSources: [], columns: [], groupBy: [], subtotals: false, grandTotal: false } }
        ),
        scope: { level: 'system' },
        sharing: preview.sharing || { sharedWith: [], isReadOnly: false },
        createdBy: 'ai-assistant',
        updatedBy: 'ai-assistant',
      });
      return newReport._id;
    } catch (error) {
      console.error('[SystemWeb] Failed to save AI report draft:', error);
      return null;
    }
  }, []);

  const handleAINavigate = useCallback((target: string, data?: AINavigateData) => {
    switch (target) {
      case 'library':
        navigate('/data-report-templates');
        break;
      case 'builder':
        if (data?.configType === 'dashboard') {
          navigate('/dashboard-templates/new', { state: data });
        } else if (data?.configType === 'document') {
          navigate('/document-templates/new', { state: data });
        } else {
          navigate('/data-report-templates/new', { state: data });
        }
        break;
      case 'report':
        if (data?.reportDefinitionId) {
          navigate(`/data-report-templates/${data.reportDefinitionId}`);
        }
        break;
      case 'dashboard':
        if (data?.dashboardId) {
          navigate(`/dashboard-templates/${data.dashboardId}`);
        } else {
          navigate('/dashboard-templates');
        }
        break;
      case 'document':
        if (data?.documentId) {
          navigate(`/document-templates/${data.documentId}`);
        } else {
          navigate('/document-templates');
        }
        break;
      default:
        console.warn(`Unknown navigation target: ${target}`);
    }
  }, [navigate]);

  // Hide boot splash when loading is complete AND the user is not authenticated
  // (login page renders immediately). When authenticated, SystemDBProvider
  // manages boot splash visibility based on DB initialization status.
  useEffect(() => {
    if (isPublicHandoff) {
      document.getElementById('boot-screen')?.classList.add('hidden');
      return;
    }
    if (!isLoading && !isAuthenticated) {
      document.getElementById('boot-screen')?.classList.add('hidden');
    }
  }, [isAuthenticated, isLoading, isPublicHandoff]);

  if (isPublicHandoff) {
    return <SsoItHandoffPage />;
  }

  if (isLoading) {
    return null; // HTML boot splash stays visible
  }

  if (!isAuthenticated) {
    return (
      <Routes>
        <Route path="/auth/callback" element={<AuthCallbackPage />} />
        <Route path="/auth/accept" element={<AuthAcceptPage />} />
        <Route path="/brand-preview" element={<BrandSplashPreviewPage />} />
        <Route path="*" element={<LoginPage />} />
      </Routes>
    );
  }

  return (
    <SystemDBProvider>
      <ThemeBootstrap>
        <AIChatProvider onNavigate={handleAINavigate} onSave={handleAISave} appContext="system">
          <Routes>
        <Route path="/auth/callback" element={<AuthCallbackPage />} />
        <Route path="/brand-preview" element={<BrandSplashPreviewPage />} />
        <Route element={<AdminLayout />}>
        <Route path="/" element={<Navigate to="/system-status" replace />} />
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/organizations" element={<OrganizationsPage />} />
        <Route path="/tenants" element={<TenantsPage />} />
        <Route path="/tenants/new" element={<CreateTenantWizard />} />
        <Route path="/tenants/new/:draftId" element={<CreateTenantWizard />} />
        <Route path="/tenants/:id" element={<DepartmentDetailPage />} />
        <Route path="/users" element={<UsersPage />} />
        <Route path="/users/invite" element={<Navigate to="/users" replace />} />
        <Route path="/users/:id" element={<SystemUserDetailPage />} />
        <Route path="/lookups" element={<Suspense fallback={<div className="p-8 flex items-center justify-center h-64"><span className="opacity-60">Loading…</span></div>}><LookupsPage /></Suspense>} />
        <Route path="/system-roles" element={<SystemRolesPage />} />
        <Route path="/tiers" element={<TiersPage />} />
        <Route path="/modules" element={<ModulesPage />} />
        <Route path="/feature-flags" element={<FeatureFlagsPage />} />
        <Route path="/portal-config" element={<PortalDefaultsPage />} />
        <Route path="/audit-logs" element={<AuditLogsPage />} />
        <Route path="/import-templates" element={<ImportTemplatesPage />} />
        <Route path="/violation-code-sets" element={<SystemViolationCodeSetsPage />} />
        {/* Phase 6 cutover — UnifiedImportWizard is now the canonical
            editor. The legacy ImportTemplateEditorPage has been
            deleted; the /v2/ preview routes have been retired. */}
        <Route path="/import-templates/new" element={<UnifiedImportWizardPreviewPage />} />
        <Route path="/import-templates/:id/edit" element={<UnifiedImportWizardPreviewPage />} />
        <Route path="/import-runs" element={<ImportRunsPage />} />
        <Route path="/import-runs/new" element={<ImportWizardPage />} />
        <Route path="/import-runs/new/:templateId" element={<ImportWizardPage />} />
        <Route path="/import-runs/:runId" element={<ImportRunDetailPage />} />
        <Route path="/system-status" element={<SystemStatusPage />} />
        <Route path="/mongodb" element={<MongoDbDashboardPage />} />
        <Route path="/ai-operations" element={<AiOperationsDashboardPage />} />
        <Route path="/sync-health" element={<SyncHealthPage />} />
        <Route path="/data-repair" element={<DataRepairPage />} />
        <Route path="/data-repair/delivery-duplicates" element={<Navigate to="/data-repair?tab=delivery-duplicates" replace />} />
        <Route path="/settings" element={<SettingsPage />} />

        {/* Inspection Template Library */}
        <Route path="/inspection-templates" element={<SystemInspectionTemplatesPage />} />
        <Route path="/inspection-templates/new" element={<SystemInspectionTemplateEditorPage />} />
        <Route path="/inspection-templates/:id" element={<SystemInspectionTemplateEditorPage />} />
        <Route path="/inspection-programs" element={<InspectionProgramsPage />} />
        <Route path="/forms/:id" element={<Navigate to="/inspection-templates" replace />} />
        
        {/* Legacy Migration */}
        <Route path="/legacy-migration" element={<Navigate to="/legacy-migration/schema" replace />} />
        <Route path="/legacy-migration/schema" element={<LegacyMigrationPage />} />
        <Route path="/legacy-migration/datasets" element={<LegacyMigrationPage />} />
        <Route path="/legacy-migration/department-runs/:runId" element={<LegacyMigrationRunDetailPage />} />
        <Route path="/legacy-migration/runs/:runId" element={<LegacyMigrationRunDetailPage />} />
        <Route path="/legacy-migration/runs" element={<LegacyMigrationPage />} />

        {/* Report Center */}
        <Route path="/data-report-templates" element={<SystemDataReportsPage />} />
        <Route path="/data-report-templates/new" element={<SystemDataReportBuilderPage />} />
        <Route path="/data-report-templates/:id" element={<SystemDataReportBuilderPage />} />
        <Route path="/dashboard-templates" element={<SystemDashboardsPage />} />
        <Route path="/dashboard-templates/new" element={<SystemDashboardBuilderPage />} />
        <Route path="/dashboard-templates/:id" element={<SystemDashboardBuilderPage />} />
        <Route path="/dashboard-templates/:id/view" element={<SystemDashboardBuilderPage mode="view" />} />
        <Route path="/dashboard-templates/:id/fullscreen" element={<SystemDashboardBuilderPage mode="fullscreen" />} />
        <Route path="/document-templates" element={<SystemDocumentsPage />} />
        <Route path="/document-templates/new" element={<SystemDocumentBuilderPage />} />
        <Route path="/document-templates/:id" element={<SystemDocumentBuilderPage />} />
        <Route path="/document-templates/upload" element={<SystemDocumentBuilderPage />} />
        <Route path="/email-templates/new" element={<SystemEmailTemplateBuilderPage />} />
        <Route path="/email-templates/:id" element={<SystemEmailTemplateBuilderPage />} />
        
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Route>
          </Routes>
        </AIChatProvider>
      </ThemeBootstrap>
    </SystemDBProvider>
  );
}

/**
 * @component App
 *
 * Purpose:
 * - Composes the root provider hierarchy for system-web.
 *
 * Props:
 * - None.
 *
 * Behavior:
 * - Wraps the routed application in auth, theme, app mode, feature tier, router, and breadcrumb providers.
 *
 * Data Source:
 * - hybrid
 *
 * Constraints:
 * - Must keep provider ordering compatible with auth-gated local DB access.
 *
 * Performance:
 * - Root providers are mounted once for the lifetime of the SPA session.
 */
export default function App() {
  return (
    <AuthProvider>
      <ThemeProvider>
        <SystemAppModeProvider>
          <FeatureTierProvider>
            <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
              <BreadcrumbProvider>
                <AppContent />
              </BreadcrumbProvider>
            </BrowserRouter>
          </FeatureTierProvider>
        </SystemAppModeProvider>
      </ThemeProvider>
    </AuthProvider>
  );
}
